import { Checkbox, FormControlLabel, Stack, TextField } from '@mui/material'
import type { Question } from '../api/generated/Question'
import { type AnswerForm, CHECKED } from './form'

const inputValue = (e: Event) => (e.target as HTMLInputElement).value

function AnswerField({
  question,
  value,
  disabled,
  onChange,
}: {
  question: Question
  value: string
  disabled?: boolean
  onChange: (value: string) => void
}) {
  const { label, kind, options, required } = question
  switch (kind) {
    case 'checkbox':
      return (
        <FormControlLabel
          control={
            <Checkbox
              required={required}
              checked={value === CHECKED}
              disabled={disabled}
              onChange={(_, checked) => onChange(checked ? CHECKED : '')}
            />
          }
          label={required ? `${label} *` : label}
        />
      )
    case 'single_choice':
      return (
        <TextField
          select
          label={label}
          required={required}
          disabled={disabled}
          value={options.includes(value) ? value : ''}
          onChange={(e) => onChange(inputValue(e))}
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
        >
          <option value="">—</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </TextField>
      )
    default:
      return (
        <TextField
          label={label}
          required={required}
          disabled={disabled}
          multiline={kind === 'long_text'}
          minRows={kind === 'long_text' ? 3 : undefined}
          value={value}
          onInput={(e) => onChange(inputValue(e))}
          slotProps={{ htmlInput: { maxLength: kind === 'long_text' ? 2000 : 200 } }}
        />
      )
  }
}

/** Inputs for the event's questions; required ones are checked by the browser on submit. */
export function AnswerFields({
  questions,
  value,
  disabled,
  onChange,
}: {
  questions: readonly Question[]
  value: AnswerForm
  disabled?: boolean
  onChange: (value: AnswerForm) => void
}) {
  return (
    <Stack spacing={2}>
      {questions.map((question) => (
        <AnswerField
          key={question.id}
          question={question}
          value={value[question.id] ?? ''}
          disabled={disabled}
          onChange={(answer) => onChange({ ...value, [question.id]: answer })}
        />
      ))}
    </Stack>
  )
}
